import { useCallback, useEffect, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useRouter } from "@tanstack/react-router";
import { requestUnavailable } from "@softmax/observatory-client/transport";
import { useBrowserSession } from "@softmax/web-features/session";
import { actualViewerQueryOptions } from "@softmax/web-features/session/records";
import { identityChanged, viewerIdentity } from "./viewer-identity";

export function useProductSession() {
  const session = useBrowserSession();
  const client = useQueryClient();
  const router = useRouter();
  const [resetting, setResetting] = useState(false);
  const viewer = useQuery({
    ...actualViewerQueryOptions(),
    enabled: !resetting,
  });
  let currentUser: string | null | undefined;

  switch (session.kind) {
    case "authenticated":
      currentUser = session.session.user.id;
      break;
    case "anonymous":
      currentUser = null;
      break;
  }

  const currentViewer = viewer.isSuccess
    ? viewerIdentity(viewer.data)
    : undefined;
  const [userIdentity, setUserIdentity] = useState<string | null>();
  const [principalIdentity, setPrincipalIdentity] = useState<string | null>();
  const replacing = useRef(false);
  const rechecking =
    userIdentity !== undefined &&
    userIdentity !== null &&
    session.session === null &&
    (session.kind === "pending" || session.kind === "unavailable");
  const changed =
    identityChanged({ previous: userIdentity, current: currentUser }) ||
    identityChanged({ previous: principalIdentity, current: currentViewer });

  const resetSession = useCallback(() => {
    if (replacing.current) return;

    replacing.current = true;
    setResetting(true);
    void client.cancelQueries();
    client.clear();
    router.clearCache();
    // A new document discards private loader data and mounted feature state.
    window.location.replace(window.location.href);
  }, [client, router]);

  if (userIdentity === undefined && currentUser !== undefined)
    setUserIdentity(currentUser);

  if (principalIdentity === undefined && currentViewer !== undefined)
    setPrincipalIdentity(currentViewer);

  if (requestUnavailable(viewer.error) && principalIdentity !== null)
    setPrincipalIdentity(null);

  useEffect(() => {
    if (changed) resetSession();
  }, [changed, resetSession]);

  return {
    session,
    client,
    viewer,
    updating: resetting || changed,
    rechecking,
    resetSession,
  };
}
